import { useMemo } from 'react';
import {
    getStatsigProviderComponent,
    isNewStatsigSDK
} from '~/common/Statsig/hybridStatsig';
import { initializeStatsigClient } from '~/common/Statsig/statsigClient';
import {
    useStatsigOptions,
    type UseStatsigOptionsProps
} from '~/common/Statsig/useStatsigOptions';

export type HybridStatsigProps = UseStatsigOptionsProps;

export const useHybridStatsig = ({
    configStore,
    networkStore,
    currUser,
    currCompany,
    statsigUserCustomOverride,
    isUnauthenticatedContext,
    mountKey = 'static'
}: HybridStatsigProps) => {
    const StatsigProvider = getStatsigProviderComponent();

    const {
        handleSuccess,
        handleError,
        statsigProviderProps,
        ...statsigOptions
    } = useStatsigOptions({
        currUser,
        configStore,
        networkStore,
        currCompany,
        statsigUserCustomOverride,
        isUnauthenticatedContext,
        mountKey
    });

    const client = useMemo(() => {
        if (isNewStatsigSDK()) {
            return initializeStatsigClient({
                sdkKey: configStore.config.statsig_client_api_key,
                user: statsigOptions.user,
                options: statsigOptions.options,
                initTimeoutMs:
                    configStore.config.statsig_initialization_timeout,
                onReady: handleSuccess,
                onError: (error, message?) => {
                    handleError(error, message || 'StatsigClient');
                }
            });
        } else {
            return null;
        }
    }, [
        configStore.config.statsig_client_api_key,
        configStore.config.statsig_initialization_timeout,
        statsigOptions.user,
        statsigOptions.options,
        handleSuccess,
        handleError
    ]);

    return {
        StatsigProvider,
        statsigProviderProps,
        client
    };
};
